import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { PageProps } from '@/lib/market/builders/shared'
import type { EquitiesData } from '@/lib/market/builders/equities'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { buildEquities } from '@/lib/market/builders/equities'
import { withMarket } from '@/lib/market/builders/shared'
import { formatKes } from '@/lib/market/format'
import { useMarketTotals } from '@/components/landing/market-pulse'
import { ChangeText } from '@/components/market/change'
import EquitiesTable from '@/components/market/equities-table'
import PageHeader from '@/components/generals/page-header'
import Unavailable from '@/components/market/unavailable'

type EquitiesProps = PageProps<EquitiesData>

export const getServerSideProps: GetServerSideProps<EquitiesProps> = async context => withMarket(snapshot => buildEquities(snapshot), { req: context.req })

const Summary = ({ data }: { data: EquitiesData }): ReactNode => {
    const totals = useMarketTotals(data.stocks)

    return (
        <dl className='flex flex-wrap gap-x-8 gap-y-3 text-sm'>
            <div>
                <dt className='text-xs text-muted-foreground'>Listed shares</dt>
                <dd className='mt-0.5 font-semibold text-foreground'>{data.stocks.length}</dd>
            </div>
            <div>
                <dt className='text-xs text-muted-foreground'>Market capitalisation</dt>
                <dd className='mt-0.5 flex items-baseline gap-2 font-semibold text-foreground'>
                    {formatKes(totals.cap)}
                    <ChangeText value={totals.capChange} className='text-xs font-medium' />
                </dd>
            </div>
            <div>
                <dt className='text-xs text-muted-foreground'>Turnover today</dt>
                <dd className='mt-0.5 font-semibold text-foreground'>{formatKes(totals.turnover)}</dd>
            </div>
            <div>
                <dt className='text-xs text-muted-foreground'>Breadth</dt>
                <dd className='mt-0.5 font-semibold'>
                    <span className='text-up'>{totals.advancers} up</span>
                    <span className='mx-1.5 text-muted-foreground'>/</span>
                    <span className='text-down'>{totals.decliners} down</span>
                    <span className='mx-1.5 text-muted-foreground'>/</span>
                    <span className='text-muted-foreground'>{totals.unchanged} flat</span>
                </dd>
            </div>
        </dl>
    )
}

const Equities: FC<EquitiesProps> = ({ data, error }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Equities'
                description='Live prices, performance, fundamentals and technical signals for every share listed on Kilele Exchange.'
            />

            {data ? (
                <>
                    <PageHeader
                        eyebrow='Price list'
                        title='Equities'
                        description='Every listed share with live prices during the session. Switch views for performance, fundamentals and technical signals, and star the shares you want to follow.'
                    >
                        <Summary data={data} />
                    </PageHeader>

                    <section aria-label='Equities table'>
                        <EquitiesTable stocks={data.stocks} sectors={data.sectors} segments={data.segments} />
                    </section>
                </>
            ) : (
                <Unavailable message={error} />
            )}
        </Layout>
    )
}

export default Equities
